import axios from "axios";
import { buildExample, buildExampleLetter } from "./examples";

// Always try the backend on the page's own origin first, including previews.
export function getApiBase({ fallback = false } = {}) {
  if (fallback && process.env.REACT_APP_BACKEND_URL) {
    try {
      const url = new URL(process.env.REACT_APP_BACKEND_URL);
      if (/^https?:$/.test(url.protocol) && url.host !== window.location.host) {
        return `${url.origin}${url.pathname.replace(/\/(?:api\/?)?$/, "")}/api`;
      }
    } catch {
      // An invalid optional fallback must not break the same-origin API.
    }
  }
  return "/api";
}

export async function apiRequest(path, options = {}) {
  const base = getApiBase();
  try {
    return await axios({ ...options, url: `${base}${path}` });
  } catch (error) {
    const networkError = error.code === "ERR_NETWORK" ||
      (!error.response && !!error.request && !axios.isCancel(error));
    const serverError = error.response?.status >= 500 && error.response?.status < 600;
    const fallback = getApiBase({ fallback: true });
    if ((!networkError && !serverError) || fallback === base) throw error;
    // Exactly one retry, retaining the method, body and timeout.
    return axios({ ...options, url: `${fallback}${path}` });
  }
}

export async function getConfig() {
  const { data } = await apiRequest("/config", { timeout: 10000 });
  return data;
}

export async function analyzeFiles(files, situation) {
  const form = new FormData();
  files.forEach((f) => form.append("files", f));
  if (situation && situation.trim()) form.append("situation", situation.trim());
  const { data } = await apiRequest("/analyze", {
    method: "POST",
    data: form,
    headers: { "X-Timezone": Intl.DateTimeFormat().resolvedOptions().timeZone },
    timeout: 90000,
  });
  return data;
}

export async function analyzeExample(exampleId) {
  return buildExample(exampleId);
}

export async function generateResponse(analysis, situation) {
  if (analysis.example_id && !situation?.trim()) {
    const letter = buildExampleLetter(analysis);
    if (letter) return letter;
  }
  const { data } = await apiRequest("/generate-response", {
    method: "POST",
    data: { analysis, situation: situation || null },
    timeout: 90000,
  });
  return data.letter;
}
